import React, { useState, useRef } from 'react';
import './DesminutadorUploader.css';
import { CloudUpload, Close, PictureAsPdf } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { addAlert } from 'store/modules/snackBar';
import { RootState } from 'store';
import { Dialog, DialogContent } from '@material-ui/core';
import { applyDesminutadorData } from 'utils/desminutadorMapper';
import { getInstrumentoTipo } from 'common/genericDescriptions';
import { InstrumentoFinanceiro } from 'common/genericTypes';

interface DesminutadorUploaderProps {
  open?: boolean;
  onClose?: () => void;
  onProcessSuccess?: (data: any) => void;
  onCancel?: () => void;
  maxFileSize?: number;
  title?: string;
  subtitle?: string;
  participantes?: any[];
  bancos?: any[];
  instrumentoTipo?: InstrumentoFinanceiro;
}

const DesminutadorUploader: React.FC<DesminutadorUploaderProps> = ({
  open = false,
  onClose,
  onProcessSuccess,
  onCancel,
  maxFileSize = 10 * 1024 * 1024,
  title = 'Preenchimento Inteligente com IA',
  subtitle = 'Importe sua minuta em PDF e deixe nossa IA preencher automaticamente os campos da escrituração',
  participantes = [],
  bancos = [],
  instrumentoTipo
}) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const postImportarMinuta = useSelector((state: RootState) => state.instrumentoApi.postImportarMinuta);
  
  
  const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file: File) => {
    if (file.type !== 'application/pdf') {
      alert('Apenas arquivos PDF são aceitos');
      return;
    }

    if (file.size > maxFileSize) {
      alert(`O arquivo deve ter no máximo ${formatFileSize(maxFileSize)}`);
      return;
    }

    setSelectedFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onProcess = async () => {
    if (!selectedFile || !postImportarMinuta) {
      return;
    }

    setIsProcessing(true);
    setProcessingStatus('Iniciando processamento...');

    try {
      const data = await postImportarMinuta(dispatch, selectedFile);
      setIsProcessing(false);

      setTimeout(() => {
        applyDesminutadorData(data.resposta, participantes, bancos, instrumentoTipo!);
        
        dispatch(addAlert({
          severity: 'success',
          message: 'Documento processado com sucesso! Os formulários foram preenchidos automaticamente.'
        }));
        
        if (onClose) {
          onClose();
        }
      }, 100);
      
      if (onProcessSuccess) {
        onProcessSuccess(data);
      }
    } catch (error) {
      setIsProcessing(false);
      console.error('Erro ao processar documento:', error);
      
      dispatch(addAlert({
        severity: 'error',
        message: error instanceof Error ? error.message : 'Erro ao processar o documento. Tente novamente.'
      }));
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="document-uploader-title"
    >
      <DialogContent>
        <div className="document-uploader">
          <div className="document-uploader-header">
            <div className="document-uploader-title">
              <span>{title}</span>
            </div>
          </div>
          
          <div className="document-uploader-content">
        {!isProcessing && !selectedFile && (
          <div className="upload-container">
            <p className="upload-description">{subtitle}</p>
            <div 
              className={`dropzone ${isDragOver ? 'dragover' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}>
              <CloudUpload className="upload-icon" />
              <h3 className="upload-title">Arraste seu PDF aqui</h3>
              <p className="upload-subtitle">ou clique para selecionar o arquivo</p>
              <p className="upload-note">Apenas arquivos PDF são aceitos</p>
            </div>
            
            <input 
              ref={fileInputRef}
              type="file" 
              accept="application/pdf"
              onChange={onFileSelected}
              style={{ display: 'none' }} />
          </div>
        )}

        {selectedFile && !isProcessing && (
          <div className="file-selected">
            <div className="file-info-card">
              <div className="file-info-content">
                <PictureAsPdf className="file-icon" />
                <div className="file-details">
                  <h4>{selectedFile.name}</h4>
                  <p>{formatFileSize(selectedFile.size)}</p>
                </div>
                <button className="remove-file-button" onClick={removeFile}>
                  <Close className="remove-icon" />
                </button>
              </div>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="processing-section">
            <div className="processing-spinner"></div>
            <h3 className="processing-title">Processando documento...</h3>
            <p className="processing-subtitle">Nossa IA está analisando sua minuta e extraindo as informações</p>
          </div>
        )}
          </div>
          
          <div className="document-uploader-actions">
            <button 
              className="cancel-button" 
              onClick={handleCancel}
              disabled={isProcessing}>
              Cancelar
            </button>
            <button 
              className="process-button" 
              onClick={onProcess}
              disabled={!selectedFile || isProcessing}>
              {isProcessing ? 'Processando...' : 'Processar'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DesminutadorUploader;
